import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

function ViewGamesPage() {
  const { teamId } = useParams();
  const [games, setGames] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamAndGames = async () => {
      setLoading(true);
      setError(null);
      try {
        // First, fetch the team name
        const teamDoc = await getDoc(doc(db, "teams", teamId));
        if (teamDoc.exists()) {
          setTeamName(teamDoc.data().name || "Your Team");
        }
        
        // Array to store all games
        let allGames = [];
        
        // Try to fetch games using the new data model first
        const newModelQuery = query(
          collection(db, "games"),
          where("teamId", "==", teamId),
          where("status", "==", "completed")
        );
        
        const newModelSnap = await getDocs(newModelQuery);
        if (!newModelSnap.empty) {
          // If we have games in the new format
          allGames = newModelSnap.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            // Make sure we have these fields for consistency
            yourScore: doc.data().yourScore,
            opponentScore: doc.data().opponentScore
          }));
        } else {
          // Fall back to the old model (home/away) if no games found in new format
          const [homeSnap, awaySnap] = await Promise.all([
            getDocs(
              query(
                collection(db, "games"),
                where("homeTeamId", "==", teamId),
                where("status", "==", "completed")
              )
            ),
            getDocs(
              query(
                collection(db, "games"),
                where("awayTeamId", "==", teamId),
                where("status", "==", "completed")
              )
            )
          ]);

          // Convert home games to consistent format
          const homeGames = homeSnap.docs.map(doc => {
            const data = doc.data();
            return { 
              id: doc.id,
              ...data,
              isHomeTeam: true,
              // Add the new model fields for consistency
              yourScore: data.homeScore,
              opponentScore: data.awayScore
            };
          });
          
          // Convert away games to consistent format
          const awayGames = awaySnap.docs.map(doc => {
            const data = doc.data();
            return { 
              id: doc.id,
              ...data,
              isHomeTeam: false,
              // Add the new model fields for consistency
              yourScore: data.awayScore,
              opponentScore: data.homeScore
            };
          });
          
          allGames = [...homeGames, ...awayGames];
        }

        // Sort games by date (most recent first)
        const sortedGames = allGames.sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );

        setGames(sortedGames);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load data: " + err.message);
      }
      setLoading(false);
    };

    fetchTeamAndGames();
  }, [teamId]);

  // Updated to use the consistent score model
  const getGameResult = (game) => {
    // Get scores consistently regardless of data model
    const yourScore = game.yourScore || 0;
    const opponentScore = game.opponentScore || 0;
    
    if (yourScore === opponentScore) {
      return { text: "Draw", color: "#64748b" };
    }
    
    return yourScore > opponentScore 
      ? { text: "Win", color: "#16a34a" }
      : { text: "Loss", color: "#dc2626" };
  };

  if (loading) return <div style={styles.loadingContainer}><div style={styles.loadingSpinner}></div> Loading games...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>{teamName} Game History</h1>
        {games.length > 0 && (
          <span style={styles.countBadge}>
            {games.length} game{games.length !== 1 && 's'}
          </span>
        )}
      </div>

      {games.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📊</div>
          <p style={styles.emptyText}>No completed games found</p>
          <div style={styles.emptyDivider} />
          <p style={styles.emptySubtext}>Completed games will appear here</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {games.map((game) => {
            const result = getGameResult(game);
            const opponentName = game.opponentName || "Opponent";
            // Use the consistent score model
            const teamScore = game.yourScore || 0;
            const opponentScore = game.opponentScore || 0;

            return (
              <div key={game.id} style={styles.card}>
                <div style={styles.resultIndicator(result.color)}></div>
                
                <div style={styles.cardHeader}>
                  <div style={styles.resultBadge(result.color)}>
                    {result.text}
                  </div>
                  <time style={styles.gameDate}>
                    {new Date(game.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                  </time>
                </div>
                
                <div style={styles.matchupContainer}>
                  <div style={styles.scoreDisplay}>
                    <span style={styles.scoreValue(true, result.color)}>{teamScore}</span>
                    <span style={styles.scoreDivider}>-</span>
                    <span style={styles.scoreValue(false)}>{opponentScore}</span>
                  </div>
                  
                  <div style={styles.teamsInfo}>
                    <div style={styles.teamWithLogo}>
                      <span style={styles.teamIndicator}>Your Team</span>
                      <span style={styles.teamName}>{teamName}</span>
                    </div>
                    <div style={styles.vsText}>vs</div>
                    <div style={styles.teamWithLogo}>
                      <span style={styles.teamIndicator}>Opponent</span>
                      <span style={styles.opponentName}>{opponentName}</span>
                    </div>
                  </div>
                </div>

                <div style={styles.detailsGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Location</span>
                    <span style={styles.detailValue}>{game.locationName || "Not specified"}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Time</span>
                    <span style={styles.detailValue}>
                      {game.time ? new Date(`2000-01-01T${game.time}`).toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit"
                      }) : "Not specified"}
                    </span>
                  </div>
                </div>

                <Link 
                  to={`/team/${teamId}/dashboard/view-stats/${game.id}`}
                  style={styles.statsLink}
                >
                  View Detailed Stats
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "900px",
    margin: "2rem auto",
    padding: "1.5rem",
    fontFamily: "'Inter', system-ui, sans-serif",
    backgroundColor: "#f8fafc",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "2rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "700",
    color: "#0D3B66",
    margin: 0,
  },
  countBadge: {
    backgroundColor: "#e0e7ff",
    color: "#4338ca",
    padding: "0.5rem 1rem",
    borderRadius: "999px",
    fontSize: "0.875rem",
    fontWeight: "600",
  },
  grid: {
    display: "grid",
    gap: "1.5rem",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "1.5rem",
    margin: "0.5rem 0",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e2e8f0",
    position: "relative",
    transition: "transform 0.2s, box-shadow 0.2s",
    overflow: "hidden",
  },
  resultIndicator: (color) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "4px",
    backgroundColor: color,
  }),
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
  },
  resultBadge: (color) => ({
    backgroundColor: color + "15",
    color,
    padding: "0.25rem 0.75rem",
    borderRadius: "999px",
    fontSize: "0.875rem",
    fontWeight: "600",
  }),
  gameDate: {
    fontSize: "0.875rem",
    color: "#64748b",
    fontWeight: "500",
  },
  matchupContainer: {
    padding: "1rem",
    backgroundColor: "#f8fafc",
    borderRadius: "10px",
    marginBottom: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  scoreDisplay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "0.5rem",
  },
  scoreValue: (isTeam, color) => ({
    fontSize: "2rem",
    fontWeight: "700",
    color: isTeam ? (color || "#0D3B66") : "#64748b",
  }),
  scoreDivider: {
    fontSize: "1.5rem",
    color: "#cbd5e1",
    margin: "0 0.5rem",
  },
  teamsInfo: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: "0.75rem",
    alignItems: "center",
  },
  teamWithLogo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.25rem",
  },
  teamIndicator: {
    fontSize: "0.7rem",
    textTransform: "uppercase",
    color: "#64748b",
    letterSpacing: "0.05em",
    fontWeight: "600",
  },
  teamName: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#0D3B66",
    textAlign: "center",
  },
  opponentName: {
    fontSize: "1rem",
    fontWeight: "500",
    color: "#334155",
    textAlign: "center",
  },
  vsText: {
    fontSize: "0.875rem",
    color: "#64748b",
    fontWeight: "600",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "1rem",
    marginBottom: "1.5rem",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  detailLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  detailValue: {
    fontSize: "0.9rem",
    color: "#0D3B66",
    fontWeight: "500",
  },
  statsLink: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#0D3B66",
    color: "#ffffff",
    textDecoration: "none",
    fontWeight: "600",
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    transition: "all 0.2s",
    marginTop: "0.5rem",
    boxShadow: "0 2px 4px rgba(13, 59, 102, 0.2)",
  },
  emptyState: {
    textAlign: "center",
    padding: "4rem 1rem",
    backgroundColor: "#f8fafc",
    borderRadius: "12px",
    border: "2px dashed #cbd5e0",
  },
  emptyIcon: {
    fontSize: "2.5rem",
    margin: "0 0 1rem",
  },
  emptyText: {
    fontSize: "1.25rem",
    color: "#64748b",
    fontWeight: "500",
    margin: 0,
  },
  emptySubtext: {
    fontSize: "0.875rem",
    color: "#94a3b8",
    margin: "0.5rem 0 0",
  },
  emptyDivider: {
    height: "1px",
    backgroundColor: "#cbd5e0",
    width: "80px",
    margin: "1rem auto",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
    padding: "3rem",
    color: "#64748b",
    fontSize: "1.125rem",
  },
  loadingSpinner: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "3px solid rgba(13, 59, 102, 0.1)",
    borderTopColor: "#0D3B66",
    animation: "spin 1s linear infinite",
  },
  error: {
    textAlign: "center",
    padding: "2rem",
    color: "#dc2626",
    backgroundColor: "#fee2e2",
    borderRadius: "8px",
    margin: "2rem",
    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.1)",
  },
};

export default ViewGamesPage;